import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Stack,
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
  TextField,
  Rating,
} from "@mui/material";
import {
  CalendarMonthOutlined,
  AccessTime,
  Person,
  ContentCut,
  Cancel,
  AccountCircle,
  Logout,
  CalendarToday,
  CheckCircleOutline,
  Edit,
  RateReview,
  ArrowBack,
  Star,
} from "@mui/icons-material";
import moment from "moment";
import axios from "axios";
import { MyDomain } from "../../App";
import { useAppContext } from "../../AppContext";
import MPHomeLoginRequired from "./MPHomeLoginRequired";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

const C = {
  primary: "#1A1A2E",
  primaryHover: "#16213E",
  accent: "#C9A84C",
  accentLight: "#FDF6E3",
  accentBorder: "rgba(201,168,76,0.25)",
  accentMuted: "rgba(201,168,76,0.12)",
  success: "#10b981",
  border: "#e5e7eb",
  textMuted: "#6b7280",
  textLight: "#9ca3af",
};

export default function MPHomeApps() {
  const history = useHistory();
  const { t } = useTranslation();
  const { User, setUser, AllData, Lang } = useAppContext();

  const getCurrencySymbol = (countryCode) => {
    const currencyMap = {
      "+972": "₪",
      "+46": "kr",
      "+1": "$",
      "+44": "£",
      "+49": "€",
    };
    return currencyMap[countryCode] || "₪";
  };

  const currencySymbol = getCurrencySymbol(User?.countryNumber);

  const [isLoading, setIsLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    appointmentId: null,
  });
  const [BarberPhoneNumber, setBarberPhoneNumber] = useState("");
  const [editNameDialog, setEditNameDialog] = useState({
    open: false,
    newName: "",
  });
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    appointmentId: null,
    barberPhoneNumber: "",
    rating: 0,
    comment: "",
  });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const GetMyApps = async () => {
    if (!User || !User.phoneNumber) return;
    try {
      const response = await axios.post(
        `${MyDomain}Users2/GetUserAppsAndServices`,
        {
          PhoneNumber: User.phoneNumber,
        },
      );
      setAppointments(response.data.myAppointments || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancelClick = (appointmentId, barberPhoneNumber) => {
    setCancelDialog({ open: true, appointmentId });
    setBarberPhoneNumber(barberPhoneNumber);
  };

  const handleCancelConfirm = async () => {
    let barberExpoToken = AllData.barberData.expoToken;
    if (
      AllData.allBarbersWithData &&
      BarberPhoneNumber !== AllData.barberData.phoneNumber
    ) {
      const appointmentBarber = AllData.allBarbersWithData.find(
        (b) => b.barber.phoneNumber === BarberPhoneNumber,
      );
      if (appointmentBarber?.barber?.expoToken)
        barberExpoToken = appointmentBarber.barber.expoToken;
    }

    const data = {
      Appointment: {
        Id: cancelDialog.appointmentId,
        Name: null,
        PhoneNumber: null,
        IsTaken: 0,
        BarberPhoneNumber: BarberPhoneNumber,
      },
      Barber: { ExpoToken: barberExpoToken },
      UserName: User.name,
      Lang: Lang,
      BookedServices: [],
      AllServices: [],
    };

    try {
      await axios.put(
        `${MyDomain}Appointments2/UpdateAppWithServicesForDelete`,
        data,
      );
      setCancelDialog({ open: false, appointmentId: null });
      GetMyApps();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancelClose = () =>
    setCancelDialog({ open: false, appointmentId: null });

  const handleEditNameClick = () =>
    setEditNameDialog({ open: true, newName: User.name || "" });
  const handleEditNameClose = () =>
    setEditNameDialog({ open: false, newName: "" });

  const handleUpdateName = async () => {
    if (!editNameDialog.newName.trim()) return;
    setIsUpdatingName(true);
    try {
      await axios.put(`${MyDomain}Users2/UpdateUserName`, {
        Id: User.id,
        Name: editNameDialog.newName.trim(),
      });
      const updatedUser = { ...User, name: editNameDialog.newName.trim() };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      handleEditNameClose();
    } catch (err) {
      alert(t("ErrorUpdatingName") || "Error updating name");
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleReviewClick = (appointmentId, barberPhoneNumber) =>
    setReviewDialog({
      open: true,
      appointmentId,
      barberPhoneNumber,
      rating: 0,
      comment: "",
    });

  const handleReviewClose = () =>
    setReviewDialog({
      open: false,
      appointmentId: null,
      barberPhoneNumber: "",
      rating: 0,
      comment: "",
    });

  const handleSubmitReview = async () => {
    if (reviewDialog.rating === 0) {
      alert(t("PleaseSelectRating") || "Please select a rating");
      return;
    }
    setIsSubmittingReview(true);
    const data = {
      BarberPhoneNumber: AllData.barberData.phoneNumber,
      UserName: User.name,
      UserPhoneNumber: User.phoneNumber,
      Comment: reviewDialog.comment.trim(),
      Rating: reviewDialog.rating,
      AppointmentId: reviewDialog.appointmentId,
    };
    try {
      await axios.post(`${MyDomain}Reviews/CreateReview`, data);
      await GetMyApps();
      handleReviewClose();
      alert(t("ReviewSubmitted") || "Thank you for your review!");
    } catch (err) {
      alert(
        t("ErrorSubmittingReview") ||
          "Error submitting review. Please try again.",
      );
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const calculateTotalPrice = (services) =>
    services.reduce((total, service) => total + parseFloat(service.price), 0);

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    setAppointments([]);
  };

  useEffect(() => {
    if (User && User.phoneNumber) GetMyApps();
  }, [User]);

  useEffect(() => {
    if (!User || !User.phoneNumber) return;

    const connection = new HubConnectionBuilder()
      .withUrl(`${MyDomain}appointmentHub`)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    const startConnection = async () => {
      try {
        await connection.start();
        await GetMyApps();
        connection.on("ReceiveBookingUpdate", (data) => {
          if (
            data?.type === "booking_created" ||
            data?.type === "booking_cancelled"
          ) {
            GetMyApps();
          }
        });
      } catch (error) {
        setTimeout(startConnection, 3000);
      }
    };

    startConnection();

    return () => {
      connection.off("ReceiveBookingUpdate");
      connection.stop();
    };
  }, [User]);

  if (!User) {
    return (
      <MPHomeLoginRequired
        title={t("PleaseLoginToView")}
        subtitle={t("SignInToAccess")}
        onLoginSuccess={GetMyApps}
      />
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="70vh"
      >
        <CircularProgress size={50} thickness={4} sx={{ color: C.accent }} />
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mt: 2, fontWeight: 500 }}
        >
          {t("LoadingYourAppointments")}
        </Typography>
      </Box>
    );
  }

  // ── Shared dialog button styles ──────────────────────────────────────────

  const dialogCancelSx = {
    textTransform: "none",
    fontWeight: 600,
    flex: 1,
    py: 1.25,
    borderColor: C.border,
    color: C.textMuted,
    "&:hover": {
      borderColor: C.accent,
      color: C.accent,
      backgroundColor: C.accentMuted,
    },
  };

  const dialogConfirmSx = {
    textTransform: "none",
    fontWeight: 700,
    flex: 1,
    py: 1.25,
    backgroundColor: C.primary,
    color: C.accent,
    "&:hover": { backgroundColor: C.primaryHover },
    "&:disabled": { backgroundColor: C.border, color: C.textLight },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        pb: isMobile ? 3 : 4,
        paddingTop: 8,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}
      >
        {/* Header */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <IconButton
            onClick={() => history.goBack()}
            sx={{
              bgcolor: C.accentMuted,
              color: C.accent,
              mb: 1.5,
              "&:hover": { bgcolor: "rgba(201,168,76,0.22)" },
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
            }}
            size={isMobile ? "small" : "medium"}
          >
            <ArrowBack fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            fontWeight="800"
            sx={{ color: C.primary, mb: 0.5, letterSpacing: "-0.3px" }}
          >
            {t("MyAppointments")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: C.textMuted, fontWeight: 500 }}
          >
            {t("ManageYourBookings")}
          </Typography>
        </Box>

        {/* User card */}
        {User && (
          <Card
            sx={{
              mb: { xs: 2, sm: 3 },
              background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryHover} 100%)`,
              color: "white",
              borderRadius: 2,
              border: `1px solid rgba(201,168,76,0.2)`,
            }}
            elevation={0}
          >
            <CardContent sx={{ py: { xs: 2, sm: 2.5 }, px: { xs: 2, sm: 3 } }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar
                    sx={{
                      bgcolor: C.accent,
                      color: C.primary,
                      width: { xs: 42, sm: 48 },
                      height: { xs: 42, sm: 48 },
                      fontWeight: "700",
                      fontSize: { xs: "18px", sm: "20px" },
                    }}
                  >
                    {User?.name?.charAt(0)?.toUpperCase() || "U"}
                  </Avatar>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography
                        variant={isMobile ? "body1" : "h6"}
                        fontWeight="700"
                        sx={{ lineHeight: 1.2 }}
                      >
                        {User.name || t("User")}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleEditNameClick}
                        sx={{
                          color: C.accent,
                          padding: "4px",
                          "&:hover": { bgcolor: "rgba(201,168,76,0.2)" },
                        }}
                      >
                        <Edit sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.8,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        color: C.accent,
                      }}
                    >
                      {User.phoneNumber}
                    </Typography>
                  </Box>
                </Stack>

                {isMobile ? (
                  <IconButton
                    onClick={handleLogout}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.1)",
                      color: "white",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    }}
                    size="medium"
                  >
                    <Logout fontSize="small" />
                  </IconButton>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<Logout />}
                    onClick={handleLogout}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.3)",
                      "&:hover": {
                        borderColor: C.accent,
                        bgcolor: "rgba(201,168,76,0.1)",
                      },
                    }}
                  >
                    {t("Logout")}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {appointments.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 6 },
              textAlign: "center",
              backgroundColor: "white",
              borderRadius: 2,
              border: `2px dashed ${C.border}`,
            }}
          >
            <CalendarToday
              sx={{ fontSize: { xs: 60, sm: 80 }, color: C.border, mb: 2 }}
            />
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="700"
              gutterBottom
              sx={{ color: C.primary }}
            >
              {t("NoAppointmentsYet")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 300, mx: "auto" }}
            >
              {t("BookYourFirst")}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={{ xs: 2, sm: 2.5 }}>
            {appointments.map((item) => {
              const { appointment, bookedServices } = item;
              const totalPrice = calculateTotalPrice(bookedServices);
              const isToday = moment(appointment.date, "DD-MM-YYYY").isSame(
                moment(),
                "day",
              );
              const isPast = moment(appointment.date, "DD-MM-YYYY").isBefore(
                moment(),
                "day",
              );
              const canReview = appointment.addReview !== 1;

              return (
                <Card
                  key={appointment.id}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: isToday
                      ? C.success
                      : isPast
                        ? C.border
                        : C.accentBorder,
                    transition: "all 0.2s ease",
                    "&:hover": { boxShadow: "0 4px 20px rgba(26,26,46,0.1)" },
                    "&:active": { transform: "scale(0.98)" },
                  }}
                >
                  {/* Today banner */}
                  {isToday && (
                    <Box
                      sx={{
                        background:
                          "linear-gradient(90deg, #10b981 0%, #059669 100%)",
                        py: 0.75,
                        px: 2,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        justifyContent="center"
                      >
                        <CheckCircleOutline
                          sx={{ fontSize: 16, color: "white" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "white",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {t("TodaysAppointment")}
                        </Typography>
                      </Stack>
                    </Box>
                  )}

                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* Date & time */}
                    <Box sx={{ mb: 2 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1.5}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CalendarMonthOutlined
                            sx={{ fontSize: 20, color: C.accent }}
                          />
                          <Typography
                            variant={isMobile ? "h6" : "h5"}
                            fontWeight="700"
                            sx={{ color: C.primary }}
                          >
                            {moment(appointment.date, "DD-MM-YYYY").format(
                              "MMM DD, YYYY",
                            )}
                          </Typography>
                        </Stack>
                        {isPast && !isToday && (
                          <Chip
                            label={t("Past")}
                            size="small"
                            sx={{
                              backgroundColor: "#f3f4f6",
                              color: C.textMuted,
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          />
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ pl: 3.5 }}
                      >
                        <AccessTime sx={{ fontSize: 16, color: C.textLight }} />
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          color="text.secondary"
                        >
                          {appointment.timeStart} - {appointment.timeEnd}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: C.textMuted,
                            px: 1,
                            py: 0.25,
                            bgcolor: "#f3f4f6",
                            borderRadius: 1,
                          }}
                        >
                          {appointment.timeLength} {t("Min")}
                        </Typography>
                      </Stack>
                    </Box>

                    {/* Services */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        mb: 2,
                        backgroundColor: "#fafbfc",
                        borderColor: C.border,
                        borderRadius: 1.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="700"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          display: "block",
                          mb: 1,
                          color: C.accent,
                        }}
                      >
                        {t("Services")}
                      </Typography>
                      <Divider sx={{ mb: 1, borderColor: C.border }} />

                      <Stack spacing={1}>
                        {bookedServices.map((service, index) => (
                          <Stack
                            key={service.id}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              py: 0.75,
                              borderBottom:
                                index !== bookedServices.length - 1
                                  ? `1px solid #f3f4f6`
                                  : "none",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <ContentCut
                                sx={{ fontSize: 16, color: C.accent }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {service.service}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              fontWeight="700"
                              sx={{ color: C.accent }}
                            >
                              {service.price} {currencySymbol}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>

                      <Divider sx={{ my: 1.5, borderColor: C.border }} />

                      {/* Total row */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          bgcolor: C.accentLight,
                          p: 1.5,
                          borderRadius: 1,
                          mx: -1.5,
                          mb: -1.5,
                          mt: 1,
                          border: `1px solid ${C.accentBorder}`,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight="700"
                          sx={{ color: C.primary }}
                        >
                          {t("Total")}
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="700"
                          sx={{ color: C.accent }}
                        >
                          {totalPrice} {currencySymbol}
                        </Typography>
                      </Stack>
                    </Paper>

                    {/* Barber info */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        mb: 2,
                        p: 1.5,
                        bgcolor: "#f9fafb",
                        borderRadius: 1,
                      }}
                    >
                      <Person sx={{ fontSize: 18, color: C.textMuted }} />
                      <Typography variant="body2" color="text.secondary">
                        {t("Barber")} {appointment.barberPhoneNumber}
                      </Typography>
                    </Stack>

                    {/* Actions */}
                    <Stack spacing={1.5}>
                      {!isPast && (
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<Cancel />}
                          onClick={() =>
                            handleCancelClick(
                              appointment.id,
                              appointment.barberPhoneNumber,
                            )
                          }
                          sx={{
                            py: { xs: 1.5, sm: 1.75 },
                            fontWeight: 700,
                            textTransform: "none",
                            bgcolor: "#ef4444",
                            boxShadow: "0 4px 12px rgba(239,68,68,0.2)",
                            "&:hover": {
                              bgcolor: "#dc2626",
                              boxShadow: "0 6px 16px rgba(239,68,68,0.3)",
                            },
                            "&:active": { transform: "scale(0.98)" },
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                          }}
                        >
                          {t("CancelAppointment")}
                        </Button>
                      )}

                      {canReview && (
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<RateReview />}
                          onClick={() =>
                            handleReviewClick(
                              appointment.id,
                              appointment.barberPhoneNumber,
                            )
                          }
                          sx={{
                            py: { xs: 1.5, sm: 1.75 },
                            fontWeight: 700,
                            textTransform: "none",
                            bgcolor: C.primary,
                            color: C.accent,
                            boxShadow: "0 4px 12px rgba(26,26,46,0.2)",
                            "&:hover": {
                              bgcolor: C.primaryHover,
                              boxShadow: "0 6px 16px rgba(26,26,46,0.3)",
                            },
                            "&:active": { transform: "scale(0.98)" },
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                          }}
                        >
                          {t("LeaveReview") || "Leave a Review"}
                        </Button>
                      )}

                      {isPast && appointment.addReview === 1 && (
                        <Paper
                          sx={{
                            p: 1.5,
                            bgcolor: "#ecfdf5",
                            border: "1px solid #a7f3d0",
                            borderRadius: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            justifyContent="center"
                          >
                            <CheckCircleOutline
                              sx={{ fontSize: 18, color: C.success }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: "#059669", fontWeight: 600 }}
                            >
                              {t("ReviewSubmitted") || "Review Submitted"}
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}

        {/* ── Cancel dialog ── */}
        <Dialog
          open={cancelDialog.open}
          onClose={handleCancelClose}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, mx: 2 } }}
        >
          {/* Gold top bar */}
          <Box
            sx={{
              height: "4px",
              background: `linear-gradient(90deg, ${C.primary}, ${C.accent})`,
            }}
          />
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight="700" sx={{ color: C.primary }}>
              {t("CancelAppointmentQuestion")}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: C.textMuted }}>
              {t("CancelConfirmation")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
            <Button
              onClick={handleCancelClose}
              variant="outlined"
              sx={dialogCancelSx}
            >
              {t("KeepIt")}
            </Button>
            <Button
              onClick={handleCancelConfirm}
              variant="contained"
              color="error"
              autoFocus
              sx={{ textTransform: "none", fontWeight: 700, flex: 1, py: 1.25 }}
            >
              {t("YesCancel")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Edit name dialog ── */}
        <Dialog
          open={editNameDialog.open}
          onClose={handleEditNameClose}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, mx: 2 } }}
        >
          <Box
            sx={{
              height: "4px",
              background: `linear-gradient(90deg, ${C.primary}, ${C.accent})`,
            }}
          />
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight="700" sx={{ color: C.primary }}>
              {t("EditName") || "Edit Name"}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              label={t("Name") || "Name"}
              value={editNameDialog.newName}
              onChange={(e) =>
                setEditNameDialog({
                  ...editNameDialog,
                  newName: e.target.value,
                })
              }
              variant="outlined"
              disabled={isUpdatingName}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": { borderColor: C.accent },
                  "&.Mui-focused fieldset": { borderColor: C.accent },
                },
                "& .MuiInputLabel-root.Mui-focused": { color: C.accent },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
            <Button
              onClick={handleEditNameClose}
              variant="outlined"
              disabled={isUpdatingName}
              sx={dialogCancelSx}
            >
              {t("Cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleUpdateName}
              variant="contained"
              disabled={isUpdatingName || !editNameDialog.newName.trim()}
              sx={dialogConfirmSx}
            >
              {isUpdatingName ? (
                <CircularProgress size={22} sx={{ color: C.accent }} />
              ) : (
                t("Save") || "Save"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Review dialog ── */}
        <Dialog
          open={reviewDialog.open}
          onClose={handleReviewClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, mx: 2 } }}
        >
          <Box
            sx={{
              height: "4px",
              background: `linear-gradient(90deg, ${C.primary}, ${C.accent})`,
            }}
          />
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight="700" sx={{ color: C.primary }}>
              {t("LeaveReview") || "Leave a Review"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: C.textMuted }}>
              {t("ShareYourExperience") ||
                "Share your experience with this service"}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3}>
              {/* Rating */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="700"
                  sx={{ mb: 1.5, color: C.primary }}
                >
                  {t("Rating") || "Rating"} *
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    p: 2,
                    bgcolor: C.accentLight,
                    borderRadius: 2,
                    border: `1px solid ${C.accentBorder}`,
                  }}
                >
                  <Rating
                    name="rating"
                    value={reviewDialog.rating}
                    onChange={(event, newValue) =>
                      setReviewDialog({ ...reviewDialog, rating: newValue })
                    }
                    size="large"
                    sx={{
                      fontSize: { xs: "2.5rem", sm: "3rem" },
                      "& .MuiRating-iconFilled": { color: C.accent },
                      "& .MuiRating-iconHover": { color: "#b8922a" },
                    }}
                  />
                </Box>
                {reviewDialog.rating > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      mt: 1,
                      color: C.accent,
                      fontWeight: "600",
                    }}
                  >
                    {reviewDialog.rating === 1 && (t("Poor") || "Poor")}
                    {reviewDialog.rating === 2 && (t("Fair") || "Fair")}
                    {reviewDialog.rating === 3 && (t("Good") || "Good")}
                    {reviewDialog.rating === 4 &&
                      (t("VeryGood") || "Very Good")}
                    {reviewDialog.rating === 5 &&
                      (t("Excellent") || "Excellent")}
                  </Typography>
                )}
              </Box>

              {/* Comment */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="700"
                  sx={{ mb: 1, color: C.primary }}
                >
                  {t("Comment") || "Comment"} ({t("Optional") || "Optional"})
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={
                    t("ShareYourThoughts") ||
                    "Share your thoughts about the service..."
                  }
                  value={reviewDialog.comment}
                  onChange={(e) =>
                    setReviewDialog({
                      ...reviewDialog,
                      comment: e.target.value,
                    })
                  }
                  variant="outlined"
                  disabled={isSubmittingReview}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": { borderColor: C.accent },
                      "&.Mui-focused fieldset": { borderColor: C.accent },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: C.accent },
                  }}
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
            <Button
              onClick={handleReviewClose}
              variant="outlined"
              disabled={isSubmittingReview}
              sx={dialogCancelSx}
            >
              {t("Cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleSubmitReview}
              variant="contained"
              disabled={isSubmittingReview || reviewDialog.rating === 0}
              sx={dialogConfirmSx}
            >
              {isSubmittingReview ? (
                <CircularProgress size={22} sx={{ color: C.accent }} />
              ) : (
                t("Submit") || "Submit Review"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
